import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Chip } from "@material-ui/core";
import { LuPencil, LuTrash2, LuMoreHorizontal } from "react-icons/lu";

import MainContainer from "../../components/MainContainer";
import {
  CrudListPage,
  CrudSearchField,
  CrudPageSizeSelect,
  CrudActionButton,
  useCrudListStyles,
} from "../../components/CrudListPage";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ContactTagListModal from "../../components/ContactTagListModal";

const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(0, 0, 0, ${alpha})`;
  const cleaned = hex.replace("#", "");
  if (cleaned.length !== 6) return hex;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getTagChipStyle = (color) => ({
  backgroundColor: hexToRgba(color, 0.14),
  color,
  border: `1px solid ${hexToRgba(color, 0.32)}`,
});

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TAGS":
      return [...state, ...action.payload];
    case "UPDATE_TAGS":
      const tag = action.payload;
      const tagIndex = state.findIndex((s) => s.id === tag.id);

      if (tagIndex !== -1) {
        state[tagIndex] = tag;
        return [...state];
      }
      return [tag, ...state];
    case "DELETE_TAGS":
      return state.filter((t) => t.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useLocalStyles = makeStyles(() => ({
  tagChip: {
    fontWeight: 600,
    borderRadius: 999,
    height: 26,
  },
  contactsCell: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
}));

const Tags = () => {
  const classes = useCrudListStyles({ tableMinWidth: 560 });
  const localClasses = useLocalStyles();
  const { user, socket } = useContext(AuthContext);

  const [selectedTagContacts, setSelectedTagContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [selectedTagName, setSelectedTagName] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchMoreTags = async () => {
        try {
          const { data } = await api.get("/tags/", {
            params: { searchParam, pageNumber, kanban: 0, limit: pageSize },
          });
          dispatch({ type: "LOAD_TAGS", payload: data.tags });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };

      if (pageNumber > 0) {
        setLoading(true);
        fetchMoreTags();
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, pageSize]);

  useEffect(() => {
    const onCompanyTags = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onCompanyTags);
    return () => {
      socket.off(`company${user.companyId}-tag`, onCompanyTags);
    };
  }, [socket, user.companyId]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPageNumber(1);
    dispatch({ type: "RESET" });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPageNumber(1);
    dispatch({ type: "RESET" });
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleShowContacts = (contacts, tag) => {
    setSelectedTagContacts(contacts);
    setContactModalOpen(true);
    setSelectedTagName(tag);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedTagContacts([]);
    setSelectedTagName("");
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prev) => prev + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <CrudListPage
        title={`${i18n.t("tags.title")} (${tags.length})`}
        onScroll={handleScroll}
        tableMinWidth={560}
        beforeContent={
          <>
            {contactModalOpen && (
              <ContactTagListModal
                open={contactModalOpen}
                onClose={handleCloseContactModal}
                tag={selectedTagName}
              />
            )}
            <ConfirmationModal
              title={
                deletingTag &&
                `${i18n.t("tags.confirmationModal.deleteTitle")}`
              }
              open={confirmModalOpen}
              onClose={() => setConfirmModalOpen(false)}
              onConfirm={() => handleDeleteTag(deletingTag.id)}
            >
              {i18n.t("tags.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <TagModal
              open={tagModalOpen}
              onClose={handleCloseTagModal}
              aria-labelledby="form-dialog-title"
              tagId={selectedTag && selectedTag.id}
              kanban={0}
            />
          </>
        }
        toolbar={
          <>
            <CrudSearchField
              classes={classes}
              value={searchParam}
              onChange={handleSearch}
              placeholder={i18n.t("contacts.searchPlaceholder")}
            />
            <CrudPageSizeSelect
              classes={classes}
              value={pageSize}
              onChange={handlePageSizeChange}
            />
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryButton}
              onClick={handleOpenTagModal}
            >
              {i18n.t("tags.buttons.add")}
            </Button>
          </>
        }
      >
        <Table size="small" stickyHeader className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("tags.table.id")}</TableCell>
              <TableCell align="center">{i18n.t("tags.table.name")}</TableCell>
              <TableCell align="center">
                {i18n.t("tags.table.contacts")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("tags.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.map((tag) => (
              <TableRow key={tag.id}>
                <TableCell align="center">{tag.id}</TableCell>
                <TableCell align="center">
                  <Chip
                    className={localClasses.tagChip}
                    style={getTagChipStyle(tag.color)}
                    label={tag.name}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <span className={localClasses.contactsCell}>
                    {tag?.contacts?.length}
                    <CrudActionButton
                      classes={classes}
                      icon={LuMoreHorizontal}
                      onClick={() => handleShowContacts(tag?.contacts, tag)}
                      disabled={tag?.contacts?.length === 0}
                      ariaLabel="Ver contatos"
                    />
                  </span>
                </TableCell>
                <TableCell align="center">
                  <CrudActionButton
                    classes={classes}
                    icon={LuPencil}
                    onClick={() => handleEditTag(tag)}
                    ariaLabel="Editar tag"
                  />
                  <CrudActionButton
                    classes={classes}
                    icon={LuTrash2}
                    onClick={() => {
                      setConfirmModalOpen(true);
                      setDeletingTag(tag);
                    }}
                    ariaLabel="Excluir tag"
                    danger
                  />
                </TableCell>
              </TableRow>
            ))}
            {loading &&
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRowSkeleton key={`skeleton-${idx}`} columns={4} />
              ))}
          </TableBody>
        </Table>
        {hasMore && (
          <div className={classes.loadMoreWrap}>
            <Button
              variant="outlined"
              color="primary"
              onClick={loadMore}
              disabled={loading}
            >
              {loading
                ? i18n.t("contacts.loading")
                : i18n.t("contacts.loadMore")}
            </Button>
          </div>
        )}
      </CrudListPage>
    </MainContainer>
  );
};

export default Tags;
